import { Button, Checkbox, Col, Divider, Input, Modal, Row, Spin, Typography } from "antd";
import { useState } from "react";
import Warning from "src/components/GlobalIconPart/Warning";
import { INVENTORIES_ARRAY, INVENTORY_LABEL_ENUM } from "src/entity/common/common.constant";
import { MonthsDisplayPascal } from "src/entity/common/unit.constant";

const { Text } = Typography;
const CheckboxGroup = Checkbox.Group;

const mapNameInventory = {
  Tank: "Storage Tank",
  "Transport & Marketing": "Transportation & Marketing",
  Wastewater: "Wastewater Treatment Plant",
};

const ApproveReport = (props: {
  open: boolean;
  selectedYear: string;
  loadingApprovalOption: boolean;
  loadingApproveAction: boolean;
  selectedMonths: string[];
  setSelectedMonths: (selectedMonths: string[]) => void;
  selectedSourceApproval: string[];
  setSelectedSourceApproval: (selectedSource: string[]) => void;
  comment: string;
  setComment: (comment: string) => void;
  optionApprovalMonthly: { label: string; value: string }[];
  onClose: () => void;
  onApprove: (selectedSourceApproval: string[], selectedMonths: string[], comment: string, action: "approved" | "reject") => void;
}) => {
  const {
    open,
    loadingApprovalOption,
    loadingApproveAction,
    selectedYear,
    selectedMonths,
    setSelectedMonths,
    selectedSourceApproval,
    setSelectedSourceApproval,
    comment,
    setComment,
    optionApprovalMonthly,
    onClose,
    onApprove,
  } = props;
  const [isWarningApproval, setIsWarningApproval] = useState<boolean>(false);
  const [showRejectError, setShowRejectError] = useState<boolean>(false);

  const onChangeSelectSource = (checkedValues: string[]) => {
    setSelectedSourceApproval(checkedValues);
    setSelectedMonths([]);
  };

  const onChangeSelectMonth = (checkedValues: string[]) => {
    setSelectedMonths(checkedValues);
  };

  const onChangeComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  const checkDisableButton = () => {
    const isSelected = selectedSourceApproval.length === 0;
    const isSelectedMonth = selectedMonths.length === 0;
    const isLoading = loadingApprovalOption || loadingApproveAction;
    const option = optionApprovalMonthly.find((option) => option.value.toString() === selectedYear.toString());

    const isEnableFugitive = option ? false : true;

    if (selectedSourceApproval.includes(INVENTORY_LABEL_ENUM.Fugitive) && selectedSourceApproval.length === 1) {
      return isSelected || isEnableFugitive || isLoading;
    } else if (selectedSourceApproval.includes(INVENTORY_LABEL_ENUM.Fugitive) && selectedSourceApproval.length > 1) {
      return isSelected || isEnableFugitive || isLoading;
    } else {
      return isSelected || isSelectedMonth || isLoading;
    }
  };

  return (
    <div>
      <Modal
        centered
        open={open}
        title={
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Text>Please select a source for approval.</Text>
            {isWarningApproval && (
              <Row className="mt-2 flex items-center justify-center text-center">
                <Warning />
                <Text type="danger">There is no approver on this report.</Text>
              </Row>
            )}
            <Divider />
          </div>
        }
        width={710}
        okText="Approve"
        cancelText="Reject"
        onCancel={onClose}
        footer={
          <Row className="w-full border-t pt-4" justify="space-between" align="middle">
            <Button
              key="reject"
              style={{
                width: "120px",
              }}
              type="primary"
              danger
              disabled={checkDisableButton()}
              onClick={() => {
                if (comment.trim() === "") {
                  setShowRejectError(true);
                  return;
                }
                onApprove(selectedSourceApproval, selectedMonths, comment, "reject");
              }}
              loading={loadingApproveAction}
            >
              Reject
            </Button>
            <Button
              key="approve"
              style={{
                width: "120px",
              }}
              type="primary"
              disabled={checkDisableButton()}
              onClick={() => onApprove(selectedSourceApproval, selectedMonths, comment, "approved")}
              loading={loadingApproveAction}
            >
              Approve
            </Button>
          </Row>
        }
      >
        <Spin spinning={loadingApprovalOption || loadingApproveAction}>
          <CheckboxGroup value={selectedSourceApproval} onChange={onChangeSelectSource}>
            <Row gutter={[8, 16]}>
              {INVENTORIES_ARRAY.map((item, index) => (
                <Col span={8} key={index}>
                  <Checkbox value={item}>{mapNameInventory[item as keyof typeof mapNameInventory] || item}</Checkbox>
                </Col>
              ))}
            </Row>
          </CheckboxGroup>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0" }}>
            <Text strong>Please select a month to send for approval</Text>
          </div>

          <div style={{ padding: "0 16px 16px", justifyContent: "center", display: "flex", alignContent: "center" }}>
            <CheckboxGroup value={selectedMonths} onChange={onChangeSelectMonth} style={{ width: "80%" }}>
              <Row gutter={[16, 16]}>
                {MonthsDisplayPascal.map((item, index) => (
                  <Col span={6} key={index} style={{ marginBottom: "8px" }}>
                    <Checkbox
                      value={item.dataIndex}
                      disabled={
                        !optionApprovalMonthly.some((month) => month.value === item.dataIndex) || selectedSourceApproval.length === 0
                      }
                    >
                      {item.title}{" "}
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </CheckboxGroup>
          </div>

          <div style={{ justifyContent: "center", display: "flex", alignContent: "center", flexDirection: "column", alignItems: "center" }}>
            <Input.TextArea
              style={{
                width: "500px",
                height: "80px",
                borderColor: showRejectError && comment.trim() === "" ? "#ff4d4f" : undefined,
              }}
              rows={4}
              onChange={(e) => {
                onChangeComment(e);
                if (showRejectError && e.target.value.trim() !== "") {
                  setShowRejectError(false);
                }
              }}
              value={comment}
            />
            {showRejectError && comment.trim() === "" && (
              <div style={{ display: "flex", justifyContent: "flex-start", width: "500px" }}>
                <Text type="danger" style={{ fontSize: "12px", marginTop: "4px" }}>
                  Please provide a reason
                </Text>
              </div>
            )}
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default ApproveReport;
